import { useState, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiPlay, HiStop, HiTrash, HiPlus } from 'react-icons/hi2';
import {
  useListTimeEntriesQuery,
  useCreateTimeEntryMutation,
  useUpdateTimeEntryMutation,
  useDeleteTimeEntryMutation,
  useStopTimeEntryMutation,
} from '@/store/sprawyApi';
import type { TimeEntryDto } from '@/types';
import { InlineEdit } from '@/components/common/InlineEdit';
import { useDateFormat, formatDurationMinutes, formatElapsedSince } from '@/utils/dates';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import TimeEntryModal from './TimeEntryModal';
import {
  TimerBadge, TimerDot, ActionButtons, DurationDisplay, TimerTableWrap,
} from './TimeEntriesTab.styled';

interface TimeEntriesTabProps {
  sprawaId: string;
}

function useElapsedTimer(startedAt: string) {
  const [, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startInterval = useCallback((node: HTMLElement | null) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (node) {
      intervalRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  return { elapsed: formatElapsedSince(startedAt), ref: startInterval };
}

function ElapsedTimer({ startedAt }: { startedAt: string }) {
  const { elapsed, ref } = useElapsedTimer(startedAt);

  return (
    <TimerBadge ref={ref}>
      <TimerDot />
      {elapsed}
    </TimerBadge>
  );
}

export default function TimeEntriesTab({ sprawaId }: TimeEntriesTabProps) {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const { data: entries = [], isLoading } = useListTimeEntriesQuery(sprawaId);
  const [createTimeEntry] = useCreateTimeEntryMutation();
  const [updateTimeEntry] = useUpdateTimeEntryMutation();
  const [deleteTimeEntry] = useDeleteTimeEntryMutation();
  const [stopTimeEntry] = useStopTimeEntryMutation();

  const [showModal, setShowModal] = useState(false);

  const runningEntry = entries.find((e) => e.isRunning);

  const handleAddManual = () => {
    setShowModal(true);
  };

  const handleModalSubmit = async (data: {
    description: string;
    durationMinutes: number;
    date: string;
    notes?: string;
  }) => {
    await createTimeEntry({ sprawaId, ...data, startTimer: false });
    setShowModal(false);
  };

  const handleSaveField = useCallback(async (entry: TimeEntryDto, field: string, value: string | number | boolean) => {
    await updateTimeEntry({
      sprawaId,
      id: entry.id,
      description: entry.description,
      durationMinutes: entry.durationMinutes,
      date: entry.date,
      notes: entry.notes ?? undefined,
      [field]: field === 'durationMinutes' ? Number(value) : value,
    }).unwrap();
  }, [sprawaId, updateTimeEntry]);

  const handleStartTimer = async () => {
    await createTimeEntry({
      sprawaId,
      description: t('timeEntries.defaultTimerDescription'),
      durationMinutes: 0,
      date: new Date().toISOString(),
      startTimer: true,
    });
  };

  const handleStopTimer = async (id: string) => {
    await stopTimeEntry({ sprawaId, id });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('timeEntries.confirmDelete'))) {
      await deleteTimeEntry({ sprawaId, id });
    }
  };

  const totalMinutes = entries
    .filter((e) => !e.isRunning)
    .reduce((sum, e) => sum + e.durationMinutes, 0);

  const timeEntryColumns: DataTableColumn<TimeEntryDto>[] = [
    {
      key: 'description',
      header: t('timeEntries.description'),
      render: (entry) =>
        entry.isRunning ? (
          entry.description
        ) : (
          <InlineEdit
            value={entry.description}
            onSave={(val: string | number | boolean) => handleSaveField(entry, 'description', val)}
            variant="text"
            required
            ariaLabel={t('timeEntries.description')}
          />
        ),
    },
    {
      key: 'duration',
      header: t('timeEntries.duration'),
      render: (entry) => (
        <DurationDisplay>
          {entry.isRunning && entry.startedAt ? (
            <ElapsedTimer startedAt={entry.startedAt} />
          ) : (
            <InlineEdit
              value={entry.durationMinutes}
              onSave={(val: string | number | boolean) => handleSaveField(entry, 'durationMinutes', val)}
              variant="number"
              min={0}
              renderDisplay={(v: string | number | boolean | null) => formatDurationMinutes(Number(v ?? 0))}
              ariaLabel={t('timeEntries.duration')}
            />
          )}
        </DurationDisplay>
      ),
    },
    {
      key: 'date',
      header: t('timeEntries.date'),
      render: (entry) => formatDate(entry.date),
    },
    {
      key: 'notes',
      header: t('timeEntries.notes'),
      render: (entry) =>
        entry.isRunning ? (
          entry.notes ?? '—'
        ) : (
          <InlineEdit
            value={entry.notes ?? null}
            onSave={(val: string | number | boolean) => handleSaveField(entry, 'notes', val)}
            variant="textarea"
            ariaLabel={t('timeEntries.notes')}
          />
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (entry) => (
        <ActionButtons>
          {entry.isRunning ? (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleStopTimer(entry.id)}
              title={t('timeEntries.stopTimer')}
            >
              <HiStop />
            </Button>
          ) : (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDelete(entry.id)}
              title={t('common.delete')}
            >
              <HiTrash />
            </Button>
          )}
        </ActionButtons>
      ),
    },
  ];

  if (isLoading) {
    return <p className="text-muted">{t('common.loading')}</p>;
  }

  return (
    <>
      <ActionButtons className="mb-3">
        <Button variant="primary" size="sm" onClick={handleAddManual}>
          <HiPlus className="me-1" />
          {t('timeEntries.addEntry')}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={runningEntry ? () => handleStopTimer(runningEntry.id) : handleStartTimer}
        >
          {runningEntry ? (
            <><HiStop className="me-1" />{t('timeEntries.stopTimer')}</>
          ) : (
            <><HiPlay className="me-1" />{t('timeEntries.startTimer')}</>
          )}
        </Button>
        {totalMinutes > 0 && (
          <span className="text-muted align-self-center ms-2">
            {t('timeEntries.total')}: <strong>{formatDurationMinutes(totalMinutes)}</strong>
          </span>
        )}
      </ActionButtons>

      <TimerTableWrap>
        <DataTable<TimeEntryDto>
          ariaLabel={t('timeEntries.title', 'Time entries')}
          rowKey={(e) => e.id}
          rows={entries}
          rowClassName={(e) => (e.isRunning ? 'is-timer-row' : undefined)}
          emptyMessage={t('timeEntries.noEntries')}
          columns={timeEntryColumns}
        />
      </TimerTableWrap>

      <TimeEntryModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        entry={null}
      />
    </>
  );
}
